// @ts-strict-ignore
import React from 'react';
import Text from 'antd/lib/typography/Text';
import { PatternToken } from '../../types';
import { antGrey } from '../styles/globalStyles';

export const exampleAndDescriptionNodeForPatternToken: (token: PatternToken) => React.ReactNode = (token) => {
    const [description, example] = exampleAndDescriptionForPatternToken(token);
    return (
        <>
            <span>{description}</span>
            {example && <i>{example}</i>}
        </>
    );
};

function exampleAndDescriptionForPatternToken(token: PatternToken): [string, string] {
    switch (token) {
        case PatternToken.Version:
            return ['Three-segment SemVer', '1.2.3'];
        case PatternToken.VersionConcise:
            return ['Allows for a three-, two- or one-segment SemVer', '1.2.3, 1.2, 1'];
        case PatternToken.VersionMajorMinor:
            return [
                'Forces two-segment SemVer with Major and Minor segments only; typically used in conjunction with a dynamic Point segment',
                '1.2',
            ];
        case PatternToken.VersionMajor:
            return ['Forces one-segment SemVer with Major segment only', '1'];
        case PatternToken.VersionSuffix:
            return [
                'Used in conjunction with {version} to add a fourth numeric segment for versioning e.g. for OTA',
                null,
            ];
        case PatternToken.VersionNext:
            return ['Three-segment SemVer for the next up version after the reference version', '1.2.3'];
        case PatternToken.VersionNextConcise:
            return ['For the next up version after the reference version, concise format', '1.2.3, 1.2, 1'];
        case PatternToken.VersionPrevious:
            return ['Three-segment SemVer for the previous version before the reference version', '1.2.3'];
        case PatternToken.VersionPreviousConcise:
            return ['For the previous version before the reference version, concise format', '1.2.3, 1.2, 1'];
        case PatternToken.ReleaseName:
            return ['Human-readable release name', 'Cinnamon, BigFeature'];
        case PatternToken.BuildAppStore:
            return ['Build number as it appears in App Store Connect or Play Console', '#12345'];
        case PatternToken.BuildCI:
            return ['Build number as it appears in CI', '#12345'];
        case PatternToken.ReleasePilot:
            return [
                'The release pilot of the current release.',
                '{releasePilot} defaults to email, use {releasePilot}[ping] to mention the user',
            ];
        case PatternToken.UpcomingReleasePilot:
            return [
                'The release pilot of the upcoming release.',
                '{upcomingReleasePilot} defaults to email, use {upcomingReleasePilot}[ping] to mention the user',
            ];
        case PatternToken.PreviousReleasePilot:
            return [
                'The release pilot of the previous release.',
                '{previousReleasePilot} defaults to email, use {previousReleasePilot}[ping] to mention the user',
            ];
        case PatternToken.ReleaseBranchName:
            return ['The branch name of the current release', null];
        case PatternToken.WorkingBranchName:
            return ['The app&apos;s working branch name', null];
        case PatternToken.Wildcard:
            return ['Any string', null];
        case PatternToken.CommitMessage:
            return ['The commit&apos;s message', null];
        case PatternToken.CommitShortHash:
            return ['The commit&apos;s short hash', null];
        case PatternToken.TargetBranchName:
            return ['The target branch name of the current release', null];
        case PatternToken.WorkItems:
            return ['Commits and linked tickets in the diff for the release', `Filterable: e.g. {workItems}[fix,!bug]`];
        case PatternToken.PRTitle:
            return ['The PR title (e.g. from cherry-picked PR)', null];
        case PatternToken.PRBody:
            return ['The PR body (e.g. from cherry-picked PR)', null];
        case PatternToken.PRTitleOrCommitMessage:
            return ['The PR title (e.g. from cherry-picked PR) or commit message', null];
        case PatternToken.PRBodyOrCommitHash:
            return ['The PR body (e.g. from cherry-picked PR) or commit hash', null];
        case PatternToken.PRNumberOrCommitHash:
            return ['The PR number (e.g. from cherry-picked PR) or commit hash', null];
        case PatternToken.AppName:
            return ['The app name', null];
        case PatternToken.AppPlatform:
            return ['The app platform', null];
        case PatternToken.WorkItemsCount:
            return ['The number of completed work items for a given release', null];
        case PatternToken.IssuesCount:
            return ['The number of tickets that were completed in a given release', null];
        case PatternToken.CommitsCount:
            return ['The number of commits that went into a given release', null];
        case PatternToken.ContributorsCount:
            return ['The number of code contributors to a given release', null];
        case PatternToken.LinesOfCodeCount:
            return ['Lines of code changed in the completed release', null];
        case PatternToken.ReleaseNotes:
            return [
                'The release notes in the specified locale(s), or `en-US` if not specified',
                `{releaseNotes}[en-US,fr-CA]`,
            ];
        case PatternToken.ReleaseNotesTerm:
            return [
                'A title/label for the release notes appropriate to their destination app store',
                'e.g., "What\'s New" or "Release notes"',
            ];
        case PatternToken.ReleaseDuration:
            return ['The duration of a given release cycle from kickoff to when the release went live', null];
        case PatternToken.ReleasedAt:
            return ['The date the release went live', null];
        case PatternToken.ReleaseWorkItemsLink:
            return ['A link to the work items for a given release in Runway', null];
        case PatternToken.ReleaseAppStoreLink:
            return ['A link to the update in the App Store', null];
        case PatternToken.ReleaseVCSReleaseLink:
            return ['A link to the release in your version control system', null];
        case PatternToken.StabilityMonitoringReleaseLink:
            return ['A link to the release in your stability monitoring tool (e.g. Sentry, Firebase)', null];
        case PatternToken.CIBuildsCount:
            return ['The number of successful CI builds in the release', null];
    }
}

interface PatternTokenHelperTextProps {
    availableTokens: PatternToken[];
}

export const PatternTokenHelperTextComponent: React.FC<PatternTokenHelperTextProps> = ({ availableTokens }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                padding: '14px 20px',
                borderRadius: 8,
            }}
        >
            <Text style={{ color: antGrey.grey7, fontSize: 14, fontWeight: 600 }}>Available tokens:</Text>
            {availableTokens.map((token) => {
                return (
                    <div
                        key={token}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Text code style={{ whiteSpace: 'nowrap' }}>
                            {token}
                        </Text>
                        <Text
                            style={{
                                color: antGrey.grey7,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                                textAlign: 'right',
                                flexShrink: 3,
                            }}
                        >
                            {exampleAndDescriptionNodeForPatternToken(token)}
                        </Text>
                    </div>
                );
            })}
        </div>
    );
};
